/**
 * Service Container - Dependency Injection Container
 * 
 * Забезпечує lazy instantiation, dependency resolution, та lifecycle management для сервісів
 */

export interface ServiceLifecycle {
    onInit?(): void | Promise<void>
    onDestroy?(): void | Promise<void>
    healthCheck?(): boolean | Promise<boolean>
}

export interface ServiceDefinition {
    name: string
    factory: (container: ServiceContainer, ...deps: any[]) => any
    dependencies?: string[]
    singleton?: boolean
    lazy?: boolean
}

export interface ServiceMetadata {
    name: string
    type: 'class' | 'factory' | 'value'
    singleton: boolean
    dependencies: string[]
    instantiated: boolean
}

/**
 * Service Container with DI support
 */
export class ServiceContainer {
    private instances = new Map<string, any>()
    private factories = new Map<string, ServiceDefinition>()
    private metadata = new Map<string, ServiceMetadata>()
    private initializing = new Set<string>() // Для запобігання циклічним залежностям

    /**
     * Реєстрація сервісу
     */
    register(definition: ServiceDefinition): void {
        this.factories.set(definition.name, definition)
        this.metadata.set(definition.name, {
            name: definition.name,
            type: 'factory',
            singleton: definition.singleton !== false,
            dependencies: definition.dependencies || [],
            instantiated: false,
        })
    }

    /**
     * Реєстрація декількох сервісів
     */
    registerMany(definitions: ServiceDefinition[]): void {
        definitions.forEach((def) => this.register(def))
    }

    /**
     * Реєстрація готового екземпляру (value)
     */
    registerValue(name: string, value: any): void {
        this.instances.set(name, value)
        this.metadata.set(name, {
            name,
            type: 'value',
            singleton: true,
            dependencies: [],
            instantiated: true,
        })
    }

    /**
     * Розв'язує (resolve) сервіс з автоматичною ін'єкцією залежностей
     */
    resolve<T = any>(name: string): T {
        // Перевіряємо, чи вже створено екземпляр
        if (this.instances.has(name)) {
            return this.instances.get(name) as T
        }

        // Перевіряємо, чи зареєстровано фабрику
        const definition = this.factories.get(name)
        if (!definition) {
            throw new Error(`Service "${name}" not found. Did you forget to register it?`)
        }

        // Перевіряємо циклічні залежності
        if (this.initializing.has(name)) {
            const chain = Array.from(this.initializing).join(' -> ')
            throw new Error(`Circular dependency detected: ${chain} -> ${name}`)
        }

        this.initializing.add(name)

        try {
            // Розв'язуємо залежності
            const deps = (definition.dependencies || []).map((dep) => this.resolve(dep))

            // Створюємо екземпляр
            const instance = definition.factory(this, ...deps)

            // Зберігаємо, якщо singleton
            if (definition.singleton !== false) {
                this.instances.set(name, instance)
                const meta = this.metadata.get(name)
                if (meta) {
                    meta.instantiated = true
                }
            }

            this.initializing.delete(name)
            return instance as T
        } catch (error) {
            this.initializing.delete(name)
            throw error
        }
    }

    /**
     * Перевіряє, чи зареєстровано сервіс
     */
    has(name: string): boolean {
        return this.factories.has(name) || this.instances.has(name)
    }

    /**
     * Перевіряє, чи створено екземпляр
     */
    isInstantiated(name: string): boolean {
        return this.instances.has(name)
    }

    /**
     * Отримує всі зареєстровані сервіси
     */
    getServices(): string[] {
        return Array.from(new Set([...this.factories.keys(), ...this.instances.keys()]))
    }

    /**
     * Отримує метадані сервісу
     */
    getMetadata(name: string): ServiceMetadata | undefined {
        return this.metadata.get(name)
    }

    /**
     * Отримує всі метадані
     */
    getAllMetadata(): ServiceMetadata[] {
        return Array.from(this.metadata.values())
    }

    /**
     * Ініціалізація сервісу (викликає onInit lifecycle hook)
     */
    async initializeService(nameOrInstance: string | any): Promise<void> {
        const instance = typeof nameOrInstance === 'string' ? this.resolve(nameOrInstance) : nameOrInstance

        if (instance && typeof instance.onInit === 'function') {
            await instance.onInit()
        }
    }

    /**
     * Ініціалізація всіх сервісів
     */
    async initializeAll(): Promise<void> {
        const services = Array.from(this.instances.values())
        await Promise.all(services.map((service) => this.initializeService(service)))
    }

    /**
     * Знищення сервісу (викликає onDestroy lifecycle hook)
     */
    async destroyService(nameOrInstance: string | any): Promise<void> {
        const instance = typeof nameOrInstance === 'string' ? this.instances.get(nameOrInstance) : nameOrInstance

        if (instance && typeof instance.onDestroy === 'function') {
            await instance.onDestroy()
        }

        if (typeof nameOrInstance === 'string') {
            this.instances.delete(nameOrInstance)
            const meta = this.metadata.get(nameOrInstance)
            if (meta) {
                meta.instantiated = false
            }
        }
    }

    /**
     * Знищення всіх сервісів
     */
    async destroyAll(): Promise<void> {
        const services = Array.from(this.instances.entries())
        await Promise.all(services.map(([name]) => this.destroyService(name)))
    }

    /**
     * Health check для сервісу
     */
    async checkHealth(name: string): Promise<boolean> {
        if (!this.instances.has(name)) {
            return false
        }

        const instance = this.instances.get(name)
        if (instance && typeof instance.healthCheck === 'function') {
            return await instance.healthCheck()
        }

        return true
    }

    /**
     * Health check для всіх сервісів
     */
    async checkAllHealth(): Promise<Map<string, boolean>> {
        const results = new Map<string, boolean>()
        const services = Array.from(this.instances.keys())

        await Promise.all(
            services.map(async (name) => {
                const healthy = await this.checkHealth(name)
                results.set(name, healthy)
            })
        )

        return results
    }

    /**
     * Очищення контейнера
     */
    clear(): void {
        this.instances.clear()
        this.factories.clear()
        this.metadata.clear()
        this.initializing.clear()
    }

    /**
     * Отримання статистики контейнера
     */
    getStatistics(): {
        registered: number
        instantiated: number
        singletons: number
        transients: number
    } {
        const registered = this.factories.size + this.instances.size
        const instantiated = this.instances.size
        const metadata = Array.from(this.metadata.values())
        const singletons = metadata.filter((m) => m.singleton).length
        const transients = metadata.filter((m) => !m.singleton).length

        return {
            registered,
            instantiated,
            singletons,
            transients,
        }
    }

    /**
     * Клонування контейнера (для тестування)
     */
    clone(): ServiceContainer {
        const cloned = new ServiceContainer()
        this.factories.forEach((def) => {
            cloned.register(def)
        })
        this.instances.forEach((value, name) => {
            cloned.registerValue(name, value)
        })
        return cloned
    }
}

/**
 * Глобальний екземпляр Service Container
 */
export const globalServiceContainer = new ServiceContainer()

/**
 * Декоратор для позначення класу як Injectable
 * @example
 * @Injectable({ singleton: true, dependencies: ['HttpClient'] })
 * class UserService { ... }
 */
export function Injectable(options?: { singleton?: boolean; dependencies?: string[] }) {
    return function <T extends { new (...args: any[]): {} }>(constructor: T) {
        const serviceName = constructor.name
        
        globalServiceContainer.register({
            name: serviceName,
            factory: (container) => {
                const deps = options?.dependencies?.map((dep) => container.resolve(dep)) || []
                return new constructor(...deps)
            },
            singleton: options?.singleton !== false,
            dependencies: options?.dependencies || [],
        })

        return constructor
    }
}

export default ServiceContainer
